import { ReactNode } from 'react'

interface PageTemplateProps {
  children?: ReactNode
  title?: ReactNode
  subtitle?: string
  isLoading?: boolean
  backButton?: ReactNode
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl' | '9xl' | 'full'
}

interface SectionProps {
  children: ReactNode
  title?: string
  actions?: ReactNode
}

const Section = ({ children, title, actions }: SectionProps) => {
  return (
    <div className="mb-8">
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h2 className="text-xl font-bold text-[var(--foreground)]">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </div>
  )
}

export const PageTemplate = ({ 
  children, 
  title, 
  subtitle, 
  isLoading, 
  backButton,
  maxWidth = '7xl'
}: PageTemplateProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    '8xl': 'max-w-[90rem]', // 1440px
    '9xl': 'max-w-[99.5rem]', // 1592px (Figma desktop content width)
    full: 'max-w-full'
  }

  return (
    <div className="px-3 py-6 md:p-6 space-y-4 md:space-y-6 pt-20 md:pt-20">
      {backButton && (
        <div className="mb-4">
          {backButton}
        </div>
      )}
      {(title || subtitle) && (
        <div className="text-center py-8">
          {title && <h1 className="text-4xl font-bold text-[var(--foreground)] mb-4">{title}</h1>}
          {subtitle && <p className="text-[var(--foreground)] opacity-60 text-lg max-w-2xl mx-auto">{subtitle}</p>}
        </div>
      )}
      <div className={`${maxWidthClasses[maxWidth]} mx-auto`}>
        {children}
      </div>
    </div>
  )
}

PageTemplate.Section = Section

export type { PageTemplateProps, SectionProps } 
